import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { fetchAdminFounder, fetchLlmSettings, patchLlmSetting } from "@/lib/api";
import {
  BarChart3, Users, Activity, TrendingDown, MessageSquare,
  Sparkles, Search, Trophy, Heart, AlertTriangle, Loader2, Lock, Cpu,
} from "lucide-react";
import { toast } from "sonner";

const WINDOWS = [
  { k: 7,  label: "7 days" },
  { k: 30, label: "30 days" },
  { k: 90, label: "90 days" },
];

export default function FounderDashboard() {
  const { user } = useAuth();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!user?.is_admin) return;
    setLoading(true);
    setErr(null);
    fetchAdminFounder(days)
      .then(setData).catch((e) => setErr(e?.response?.status === 403 ? "forbidden" : "load_error"))
      .finally(() => setLoading(false));
  }, [user, days]);

  if (!user) {
    return <div className="fcc-card p-12 text-center text-[var(--fcc-muted)]">Loading…</div>;
  }
  if (!user.is_admin) {
    return (
      <div data-testid="founder-forbidden" className="fcc-card p-12 text-center">
        <Lock className="w-8 h-8 mx-auto text-[var(--fcc-muted)] mb-3" />
        <p className="font-display text-xl text-[var(--fcc-ink)]">Founder dashboard is restricted</p>
        <p className="text-sm text-[var(--fcc-ink-soft)] mt-2">
          This view is for KidHQ founders/admins only. Ask the team to add your email to <code>ADMIN_EMAILS</code>.
        </p>
        <Link to="/app" className="fcc-btn-primary mt-5 inline-block text-sm py-2 px-4">Back to dashboard</Link>
      </div>
    );
  }

  return (
    <div data-testid="founder-dashboard" className="space-y-6">
      <header className="fcc-card-accent p-6 sm:p-8">
        <p className="label-overline flex items-center gap-2"><BarChart3 className="w-3 h-3" /> Founder cockpit</p>
        <h1 className="font-display text-3xl text-[var(--fcc-ink)] mt-2">Run KidHQ on data, not assumptions</h1>
        <p className="text-sm text-[var(--fcc-ink-soft)] mt-2 max-w-2xl">
          Real beta usage, adoption, feedback, and family-health audit — refreshed every page load.
        </p>
        <div className="mt-5 flex flex-wrap gap-2" data-testid="founder-window-picker">
          {WINDOWS.map((w) => (
            <button
              key={w.k}
              data-testid={`founder-window-${w.k}`}
              onClick={() => setDays(w.k)}
              className={`fcc-pill text-xs ${
                days === w.k
                  ? "bg-[var(--fcc-ink)] text-white"
                  : "bg-white border border-[var(--fcc-border)] text-[var(--fcc-ink-soft)]"
              }`}
            >
              {w.label}
            </button>
          ))}
        </div>
      </header>

      {loading ? (
        <div className="fcc-card p-16 text-center text-[var(--fcc-muted)]">
          <Loader2 className="w-5 h-5 mx-auto animate-spin" />
          <p className="text-sm mt-3">Crunching numbers…</p>
        </div>
      ) : err ? (
        <div className="fcc-card p-12 text-center text-sm text-[var(--fcc-muted)]" data-testid="founder-error">
          Could not load admin data. {err === "forbidden" ? "Your account lost admin privileges." : "Try again in a moment."}
        </div>
      ) : !data ? null : (
        <>
          <UsageSection usage={data.usage} />
          <LlmRouterSection />
          <AdoptionSection adoption={data.adoption} />
          <FeedbackSection feedback={data.feedback} />
          <HealthSection health={data.family_health} />
          <TopsSection
            searches={data.top_searches}
            recClicks={data.top_recommendation_clicks}
            stories={data.top_stories}
          />
        </>
      )}
    </div>
  );
}

function StatTile({ Icon, label, value, hint }) {
  return (
    <div className="fcc-card p-4">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-3.5 h-3.5 text-[var(--fcc-green)]" />
        <p className="label-overline">{label}</p>
      </div>
      <p className="font-display text-3xl text-[var(--fcc-ink)]">{value}</p>
      {hint && <p className="text-[10px] uppercase tracking-widest text-[var(--fcc-muted)] mt-1">{hint}</p>}
    </div>
  );
}

function UsageSection({ usage }) {
  return (
    <section data-testid="founder-usage-section" className="space-y-4">
      <header>
        <p className="label-overline">Usage</p>
        <h2 className="font-display text-2xl text-[var(--fcc-ink)] mt-1.5">Active families & feature mix</h2>
      </header>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile Icon={Users}    label="DAU"  value={usage.daily_active_families}   hint="last 24h" />
        <StatTile Icon={Users}    label="WAU"  value={usage.weekly_active_families}  hint="last 7d" />
        <StatTile Icon={Users}    label="MAU"  value={usage.monthly_active_families} hint={`last ${usage.window_days}d`} />
        <StatTile Icon={Activity} label="Avg session depth" value={usage.average_session_depth} hint="events / user-day" />
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <FeatureList title="Most used features" items={usage.most_used_features} testid="usage-most-used" />
        <FeatureList title="Least used features" items={usage.least_used_features} testid="usage-least-used" />
      </div>
    </section>
  );
}

function FeatureList({ title, items, testid }) {
  return (
    <article data-testid={testid} className="fcc-card p-5">
      <p className="label-overline">{title}</p>
      {items.length === 0 ? (
        <p className="text-sm text-[var(--fcc-muted)] mt-3">No events in this window.</p>
      ) : (
        <ul className="mt-3 space-y-2">
          {items.map((row) => (
            <li key={row.event_type} className="flex items-center justify-between text-sm">
              <span className="text-[var(--fcc-ink)]">{row.label}</span>
              <span className="font-semibold text-[var(--fcc-ink-soft)]">{row.count}</span>
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}

function AdoptionSection({ adoption }) {
  const max = Math.max(1, ...adoption.stages.map((s) => s.count));
  return (
    <section data-testid="founder-adoption-section" className="space-y-4">
      <header className="flex items-baseline justify-between">
        <div>
          <p className="label-overline">Adoption funnel</p>
          <h2 className="font-display text-2xl text-[var(--fcc-ink)] mt-1.5">Where the funnel leaks</h2>
        </div>
        {adoption.biggest_drop && (
          <span className="fcc-pill text-xs bg-rose-50 text-rose-700 border border-rose-200">
            <TrendingDown className="w-3 h-3 inline-block mr-1" />
            Biggest drop: {adoption.biggest_drop.label} ({adoption.biggest_drop.drop_off_pct}%)
          </span>
        )}
      </header>
      <ul className="fcc-card p-5 space-y-3" data-testid="adoption-stages">
        {adoption.stages.map((s) => (
          <li key={s.key} data-testid={`adoption-stage-${s.key}`}>
            <div className="flex items-baseline justify-between mb-1">
              <span className="font-semibold text-sm text-[var(--fcc-ink)]">{s.label}</span>
              <span className="text-xs text-[var(--fcc-muted)]">
                {s.count} ({s.conversion_from_signup}% of signups · {s.conversion_from_prev}% of prev)
              </span>
            </div>
            <div className="h-2 rounded-full bg-[var(--fcc-cream)] overflow-hidden">
              <div className="h-full bg-[var(--fcc-green)]" style={{ width: `${(s.count / max) * 100}%` }} />
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function FeedbackSection({ feedback }) {
  return (
    <section data-testid="founder-feedback-section" className="space-y-4">
      <header>
        <p className="label-overline">Feedback</p>
        <h2 className="font-display text-2xl text-[var(--fcc-ink)] mt-1.5">What families are telling us</h2>
      </header>
      {feedback.by_surface.length === 0 ? (
        <div className="fcc-card p-8 text-sm text-[var(--fcc-muted)] text-center" data-testid="feedback-empty">
          No feedback received yet.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {feedback.by_surface.map((b) => (
            <article key={b.surface} data-testid={`feedback-surface-${b.surface}`} className="fcc-card p-4">
              <p className="label-overline">{b.surface.replace("_", " ")}</p>
              <p className="font-display text-2xl text-[var(--fcc-ink)] mt-1">{b.positive_rate}%</p>
              <p className="text-[10px] uppercase tracking-widest text-[var(--fcc-muted)] mt-1">positive</p>
              <p className="text-xs text-[var(--fcc-ink-soft)] mt-2">{b.up} 👍 · {b.down} 👎 · {b.total} total</p>
            </article>
          ))}
        </div>
      )}
      {feedback.recent_comments.length > 0 && (
        <article className="fcc-card p-5" data-testid="feedback-comments">
          <p className="label-overline flex items-center gap-2"><MessageSquare className="w-3 h-3" /> Recent comments</p>
          <ul className="mt-3 space-y-3">
            {feedback.recent_comments.slice(0, 10).map((c, idx) => (
              <li key={idx} className="text-sm text-[var(--fcc-ink)] border-l-2 border-[var(--fcc-border)] pl-3">
                <p>{c.comment}</p>
                <p className="text-[10px] uppercase tracking-widest text-[var(--fcc-muted)] mt-1">
                  {c.surface.replace("_", " ")} · {c.vote === "up" ? "positive" : "negative"} ·{" "}
                  {(c.created_at || "").slice(0, 10)}
                </p>
              </li>
            ))}
          </ul>
        </article>
      )}
    </section>
  );
}

function HealthSection({ health }) {
  const s = health.summary;
  return (
    <section data-testid="founder-health-section" className="space-y-4">
      <header>
        <p className="label-overline">Family health audit</p>
        <h2 className="font-display text-2xl text-[var(--fcc-ink)] mt-1.5">Beta families needing intervention</h2>
      </header>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatTile Icon={Heart}          label="Total families"   value={s.total} />
        <StatTile Icon={AlertTriangle}  label="No syncs"         value={s.no_syncs} />
        <StatTile Icon={AlertTriangle}  label="No memories"      value={s.no_memories} />
        <StatTile Icon={AlertTriangle}  label="No stories"       value={s.no_stories} />
        <StatTile Icon={TrendingDown}   label="Declining"        value={s.declining} />
      </div>
      <article className="fcc-card p-5" data-testid="health-table">
        <p className="label-overline">Families ranked by severity</p>
        {health.families.length === 0 ? (
          <p className="text-sm text-[var(--fcc-muted)] mt-3">No families yet.</p>
        ) : (
          <div className="overflow-x-auto mt-3">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-[var(--fcc-muted)] text-left">
                  <th className="py-2 pr-3">Family</th>
                  <th className="py-2 pr-3">Flags</th>
                  <th className="py-2 pr-3">Recent / Prev</th>
                  <th className="py-2 pr-3">Memories</th>
                  <th className="py-2 pr-3">Stories</th>
                </tr>
              </thead>
              <tbody>
                {health.families.slice(0, 25).map((f) => (
                  <tr key={f.user_id} data-testid={`health-row-${f.user_id}`} className="border-t border-[var(--fcc-border)]">
                    <td className="py-2 pr-3">
                      <p className="font-semibold text-[var(--fcc-ink)]">{f.name || f.email}</p>
                      <p className="text-[10px] uppercase tracking-widest text-[var(--fcc-muted)]">{f.email}</p>
                    </td>
                    <td className="py-2 pr-3">
                      {f.flags.length === 0 ? (
                        <span className="text-[var(--fcc-green)] text-xs">healthy</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {f.flags.map((flag) => (
                            <span key={flag} className="text-[10px] px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200">
                              {flag.replace("_", " ")}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-[var(--fcc-ink-soft)]">{f.recent_events} / {f.previous_events}</td>
                    <td className="py-2 pr-3 text-[var(--fcc-ink-soft)]">{f.memories}</td>
                    <td className="py-2 pr-3 text-[var(--fcc-ink-soft)]">{f.stories}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </article>
    </section>
  );
}

function TopsSection({ searches, recClicks, stories }) {
  return (
    <section data-testid="founder-tops-section" className="grid md:grid-cols-3 gap-4">
      <TopList Icon={Search}  title="Top searches"            items={searches.map((s) => ({ key: s.q,                 count: s.count, label: s.q }))} testid="top-searches" />
      <TopList Icon={Trophy}  title="Top recommendation clicks" items={recClicks.map((r) => ({ key: r.rec_key,        count: r.count, label: r.rec_key.replace("_", " ") }))} testid="top-rec-clicks" />
      <TopList Icon={Sparkles} title="Top stories"            items={stories.map((s) => ({ key: `${s.year}-${s.month}`, count: s.count, label: `${s.year}-${String(s.month).padStart(2, "0")}` }))} testid="top-stories" />
    </section>
  );
}

function TopList({ Icon, title, items, testid }) {
  return (
    <article data-testid={testid} className="fcc-card p-4">
      <p className="label-overline flex items-center gap-2"><Icon className="w-3 h-3" /> {title}</p>
      {items.length === 0 ? (
        <p className="text-xs text-[var(--fcc-muted)] mt-3">No data yet.</p>
      ) : (
        <ul className="mt-3 space-y-1.5">
          {items.map((row, idx) => (
            <li key={`${row.key}-${idx}`} className="flex items-center justify-between text-sm">
              <span className="text-[var(--fcc-ink)] truncate pr-2">{row.label}</span>
              <span className="font-semibold text-[var(--fcc-ink-soft)]">{row.count}</span>
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}

const FEATURE_LABELS = {
  copilot:        { title: "Parent Copilot",       hint: "Powers the floating drawer + /app/copilot" },
  family_story:   { title: "Family Story engine",  hint: "Monthly narrative + yearbook chapters" },
  categorization: { title: "Email categorizer",    hint: "Every incoming child-related email" },
};

function LlmRouterSection() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(null); // feature key
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLlmSettings()
      .then(setData).catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  const changeModel = async (feature, model_key) => {
    setSaving(feature);
    try {
      const next = await patchLlmSetting(feature, model_key);
      setData(next);
      toast.success(`${FEATURE_LABELS[feature]?.title || feature} → ${next.features[feature].label}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save model choice.");
    } finally {
      setSaving(null);
    }
  };

  return (
    <section data-testid="founder-llm-router-section" className="space-y-4">
      <header>
        <p className="label-overline flex items-center gap-2"><Cpu className="w-3 h-3" /> LLM Router</p>
        <h2 className="font-display text-2xl text-[var(--fcc-ink)] mt-1.5">Which model powers each feature</h2>
        <p className="text-sm text-[var(--fcc-ink-soft)] mt-1 max-w-2xl">
          Swap between Claude and Gemini per feature. Every AI response tells the parent which model answered.
        </p>
      </header>
      {loading || !data ? (
        <div className="fcc-card p-8 text-center text-sm text-[var(--fcc-muted)]">
          <Loader2 className="w-4 h-4 inline-block animate-spin mr-2" /> Loading…
        </div>
      ) : (
        <div className="grid md:grid-cols-3 gap-3">
          {Object.entries(data.features).map(([feature, chosen]) => (
            <article
              key={feature}
              data-testid={`llm-router-feature-${feature}`}
              className="fcc-card p-5"
            >
              <p className="label-overline">{FEATURE_LABELS[feature]?.title || feature}</p>
              <p className="text-xs text-[var(--fcc-muted)] mt-1">{FEATURE_LABELS[feature]?.hint}</p>
              <div className="mt-4 space-y-1.5">
                {data.available_models.map((m) => {
                  const isActive = m.key === chosen.key;
                  return (
                    <button
                      key={m.key}
                      type="button"
                      data-testid={`llm-router-choice-${feature}-${m.key}`}
                      disabled={saving === feature || isActive}
                      onClick={() => changeModel(feature, m.key)}
                      className={`w-full text-left px-3 py-2 rounded-lg border text-sm transition ${
                        isActive
                          ? "bg-[var(--fcc-ink)] text-white border-transparent"
                          : "bg-white border-[var(--fcc-border)] text-[var(--fcc-ink-soft)] hover:bg-[var(--fcc-cream-2)]"
                      }`}
                    >
                      {saving === feature && isActive
                        ? <Loader2 className="w-3.5 h-3.5 inline-block animate-spin mr-1.5" />
                        : null}
                      <span className="font-semibold">{m.label}</span>
                      <span className={`ml-2 text-[10px] uppercase tracking-widest ${isActive ? "text-white/60" : "text-[var(--fcc-muted)]"}`}>
                        {m.family}
                      </span>
                    </button>
                  );
                })}
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
